// src/component-files/forms/IncomeEntry.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Typography,
    CircularProgress,
    useTheme,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { tokens } from "../../theme";

const API_INCOMES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes";
const API_UPLOAD = "https://media.swancreate.com/upload";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_subcategories";
const API_SCHOOL_MST = "https://figazuresql.swancreate.com/api/swa/SchoolMst";

export default function IncomeEntry({ onClose, onSaved }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";

    const blank = {
        tempId: uuidv4(),
        siteId: "",
        categoryId: "",
        subcategoryId: "",
        incomeDate: "",
        description: "",
        amount: "",
        file: null,
        uploading: false,
    };

    const [rows, setRows] = useState([{ ...blank }]);
    const [schools, setSchools] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        Promise.all([
            axios.get(API_SCHOOL_MST),
            axios.get(API_CATEGORIES),
            axios.get(API_SUBCATEGORIES),
        ])
            .then(([sRes, cRes, scRes]) => {
                // only keep those categories with Type === "Income"
                setSchools(sRes.data.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName)));
                setCategories(cRes.data.filter(c => c.Type === "Income"));
                setSubcats(scRes.data);
            })
            .catch(err => {
                console.error(err);
                setError("Lookup load failed");
            })
            .finally(() => setLoading(false));
    }, []);

    const addRow = () => setRows(r => [...r, { ...blank, tempId: uuidv4() }]);
    const removeRow = id => setRows(r => r.filter(x => x.tempId !== id));
    const handleChange = (id, field, val) => {
        setRows(r =>
            r.map(row =>
                row.tempId === id
                    ? {
                        ...row,
                        [field]: val,
                        ...(field === "categoryId" ? { subcategoryId: "" } : {}),
                    }
                    : row
            )
        );
    };

    const uploadFile = async file => {
        const ext = file.name.slice(file.name.lastIndexOf("."));
        const name = `${uuidv4()}${ext}`;
        const fd = new FormData();
        fd.append("file", file, name);
        fd.append("folder", "figfinance");
        await axios.post(API_UPLOAD, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return `https://media.swancreate.com/images/figfinance/${name}`;
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            for (let row of rows) {
                let imageURL = "";
                if (row.file) {
                    imageURL = await uploadFile(row.file);
                }
                const payload = {
                    IncomeDate: row.incomeDate,
                    Amount: parseFloat(row.amount) || 0,
                    IncomeCategoryID: row.categoryId,
                    IncomeCategory:
                        categories.find(c => c.CategoryID === row.categoryId)?.CategoryName || "",
                    IncomeSubCategoryID: row.subcategoryId,
                    IncomeSubcategory:
                        subcats.find(sc => sc.SubcategoryID === row.subcategoryId)?.SubcategoryName || "",
                    Description: row.description,
                    SchoolId: row.siteId,
                    SiteName:
                        schools.find(s => s.SchoolId === row.siteId)?.SchoolName || "",
                    ImageURL: imageURL,
                };
                await axios.post(API_INCOMES, payload);
            }
            onSaved?.();
            onClose();
        } catch (err) {
            console.error(err);
            setError("Save failed, check console.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress />;

    const evenBg = isDark ? colors.primary[600] : theme.palette.action.hover;
    const oddBg = isDark ? colors.primary[700] : theme.palette.background.default;

    return (
        <Box
            p={2}
            sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderRadius: 2,
                width: "100%",
                height: "100%",
                overflow: "auto",
            }}
        >
            <Typography variant="h6" mb={2}>
                Enter New Incomes
            </Typography>
            {error && <Typography color="error" mb={2}>{error}</Typography>}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Site</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Subcategory</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Attachment</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, idx) => (
                        <TableRow
                            key={row.tempId}
                            sx={{ backgroundColor: idx % 2 === 0 ? evenBg : oddBg }}
                        >
                            <TableCell>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={row.siteId}
                                        onChange={e => handleChange(row.tempId, "siteId", e.target.value)}
                                    >
                                        <MenuItem value="">�</MenuItem>
                                        {schools.map(s => (
                                            <MenuItem key={s.SchoolId} value={s.SchoolId}>
                                                {s.SchoolName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={row.categoryId}
                                        onChange={e => handleChange(row.tempId, "categoryId", e.target.value)}
                                    >
                                        <MenuItem value="">�</MenuItem>
                                        {categories.map(c => (
                                            <MenuItem key={c.CategoryID} value={c.CategoryID}>
                                                {c.CategoryName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={row.subcategoryId}
                                        onChange={e =>
                                            handleChange(row.tempId, "subcategoryId", e.target.value)
                                        }
                                        disabled={!row.categoryId}
                                    >
                                        <MenuItem value="">�</MenuItem>
                                        {subcats
                                            .filter(sc => sc.CategoryID === row.categoryId)
                                            .map(sc => (
                                                <MenuItem key={sc.SubcategoryID} value={sc.SubcategoryID}>
                                                    {sc.SubcategoryName}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={row.incomeDate}
                                    onChange={e =>
                                        handleChange(row.tempId, "incomeDate", e.target.value)
                                    }
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    value={row.description}
                                    onChange={e =>
                                        handleChange(row.tempId, "description", e.target.value)
                                    }
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ step: "0.01" }}
                                    value={row.amount}
                                    onChange={e => handleChange(row.tempId, "amount", e.target.value)}
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <input
                                    type="file"
                                    style={{ display: "none" }}
                                    id={`file-${row.tempId}`}
                                    onChange={e => handleChange(row.tempId, "file", e.target.files[0])}
                                />
                                <label htmlFor={`file-${row.tempId}`}>
                                    <Button
                                        component="span"
                                        size="small"
                                        variant="contained"
                                        disabled={row.uploading}
                                        sx={{
                                            backgroundColor: theme.palette.success.main,
                                            color: theme.palette.common.white,
                                            "&:hover": { backgroundColor: theme.palette.success.dark },
                                        }}
                                    >
                                        {row.file ? row.file.name : "Choose�"}
                                    </Button>
                                </label>
                            </TableCell>
                            <TableCell>
                                <IconButton onClick={() => removeRow(row.tempId)}>
                                    <RemoveCircleOutline />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell colSpan={8} align="center">
                            <IconButton onClick={addRow}>
                                <AddCircleOutline /> Add Line
                            </IconButton>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        "&:hover": { backgroundColor: theme.palette.primary.dark },
                    }}
                >
                    {saving ? <CircularProgress size={20} /> : "Save All"}
                </Button>
            </Box>
        </Box>
    );
}
